/**
 * L'ID di gioco dell'ALTRO lato di un incontro (ADR-0169, lotto T6 del fronte eFootball).
 *
 * In un gioco senza integrazione (eFootball) la partita si organizza fuori da QLASH: per giocarla
 * bisogna trovarsi nel gioco, e senza l'ID dell'altro lato restava solo la chat. La lettura e' di
 * qTournaments, `tournament.match.game-ids.read` [forma decisa da @gionko, kb#292
 * 2026-09-27T18:15:55Z], ed e' riservata:
 * - la vedono solo le due parti e lo staff; agli altri arriva un RIFIUTO, non un campo vuoto;
 * - solo a incontro nato e NON concluso (`state === 'pending'` del motore, che ha due soli stati: `pending` e
 *   `settled`): a risultato confermato o a tavolino e' un rifiuto (409). ⚠️ Un torneo annullato o abbandonato
 *   lascia i suoi incontri `pending`, e li' il motore RISPONDE: per questo la scheda passa `torneoTerminato`;
 * - porta tutti i membri di ciascun lato: `gameTag` = `externalPlayerId || meta.provider.playerTag`,
 *   `gameName` = il `displayName` del profilo, `null` se mancano. Il nome QLASH non fa da ripiego.
 *
 * ⚠️ La parte del lato e' l'unico modo di sapere quale lato e' l'ALTRO, e la si confronta con
 * `playerA`/`playerB` dell'incontro (id di parte, ADR-0017), non con la posizione nella lista:
 * `home`/`away` e `a`/`b` sono due vocabolari diversi, e dedurre l'uno dall'altro e' un'identita'
 * scommessa. Se la parte non arriva, il blocco non si mostra.
 * ⚠️ Il motore la chiama `partyId` [C qTournaments `b355f82`, `incontri.ts:1214`], il commento su kb#292
 * la chiamava `party`: si leggono tutte e due, finche' il BFF non ha fissato la sua.
 *
 * ⚠️ `identity: 'non-disponibile'` [C stesso punto] dice che il motore NON ha potuto leggere i profili
 * (app-games assente, o il cancello `playerManage` spento): li' un `gameTag` nullo non vuol dire «non
 * l'ha scritto». Solo con `identity: 'letta'` un lato senza dati si puo' affermare vuoto.
 */
import type { LimuloMatch } from '../../types/limulo.ts';
import { matchStateOf } from './match.ts';

export interface LimuloGameIdMember {
  user: string;
  gameTag: string | null;
  gameName: string | null;
}

export interface LimuloGameIdSide {
  side: string;
  party: string;
  members: LimuloGameIdMember[];
}

export interface LimuloMatchGameIds {
  /** `'letta'`: i profili sono stati letti. Altro valore o assente: non si sa (v. il cappello). */
  identity: 'letta' | 'non-disponibile' | null;
  sides: LimuloGameIdSide[];
}

const oggetto = (v: unknown): Record<string, unknown> | null =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

const stringa = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Una stringa vuota o di soli spazi e' «non l'ha detto», come `null`: non si mostra un ID vuoto. */
const testoOppureNull = (v: unknown): string | null => {
  const s = stringa(v);
  return s === '' ? null : s;
};

/**
 * La risposta della lettura, gia' srotolata dalla busta. Difensiva come ogni parser della sezione:
 * una forma che non riconosce e' `null` («non lo so»), mai una lista vuota inventata.
 */
export function gameIdsOf(raw: unknown): LimuloMatchGameIds | null {
  const r = oggetto(raw);
  if (r === null || !Array.isArray(r.sides)) return null;
  const sides: LimuloGameIdSide[] = [];
  for (const s of r.sides) {
    const lato = oggetto(s);
    if (lato === null) continue;
    const members: LimuloGameIdMember[] = [];
    for (const m of Array.isArray(lato.members) ? lato.members : []) {
      const membro = oggetto(m);
      if (membro === null) continue;
      members.push({
        user: stringa(membro.user),
        gameTag: testoOppureNull(membro.gameTag),
        gameName: testoOppureNull(membro.gameName),
      });
    }
    // `partyId` e' il nome del motore; `party` quello annunciato su kb#292.
    sides.push({
      side: stringa(lato.side),
      party: stringa(lato.partyId) || stringa(lato.party),
      members,
    });
  }
  const identity = r.identity === 'letta' || r.identity === 'non-disponibile' ? r.identity : null;
  return { identity, sides };
}

/** Gli stati in cui l'incontro e' nato e non e' concluso: sono quelli in cui il server risponde. */
const STATI_CON_ID = new Set(['pending', 'proposed', 'disputed']);

/**
 * Si CHIEDE solo quando il server puo' rispondere di si': incontro con tutte e due le parti, non
 * concluso, e io sono una delle due (`isMe`). Allo spettatore la lettura sarebbe un rifiuto certo,
 * cioe' una richiesta inutile a ogni apertura della scheda.
 * ⚠️ `isMe` su entrambi i lati e' un dato incoerente: non si sceglie un lato a caso.
 */
export function idAvversarioChiedibile(match: LimuloMatch): boolean {
  if (!STATI_CON_ID.has(matchStateOf(match))) return false;
  const ioA = match.players.a?.isMe === true;
  const ioB = match.players.b?.isMe === true;
  if (ioA === ioB) return false;
  return match.playerA !== '' && match.playerB !== '';
}

/**
 * I membri dell'ALTRO lato che hanno almeno un dato da mostrare, oppure `null` quando non si sa
 * quale lato sia (non sono parte, o nessun lato della risposta porta la parte che non sono io).
 * Una lista VUOTA e' un'altra cosa: l'altro lato c'e', e nel profilo non ha scritto niente.
 */
export function altroLatoOf(
  ids: LimuloMatchGameIds,
  match: LimuloMatch,
): LimuloGameIdMember[] | null {
  if (!idAvversarioChiedibile(match)) return null;
  const altra = match.players.a?.isMe === true ? match.playerB : match.playerA;
  const lato = ids.sides.find((s) => s.party !== '' && s.party === altra);
  if (lato === undefined) return null;
  return lato.members.filter((m) => m.gameTag !== null || m.gameName !== null);
}
